import type { PartialBlock } from '@blocknote/core';

import { filterDeferredCustomBlocks } from '../deferred-custom-blocks';
import type {
  PartialBlockContent,
  PartialBlockWithLegacyTableContent,
} from './block-content-types';
import { sanitizeInlineContent } from './inline-content-normalization';
import { stripReadOnlyProblemBlocks } from './read-only-block-normalization';
import {
  normalizeRenderableTableContent,
  resolveTableContentFromBlock,
} from './table-content-normalization';

const INLINE_CONTENT_BLOCK_TYPES = new Set<string>([
  'paragraph',
  'heading',
  'bulletListItem',
  'numberedListItem',
  'checkListItem',
  'blockquote',
]);

/**
 * Converts persisted blocks into a shape BlockNote can mount without throwing.
 * This keeps invalid table shells, nested inline blocks, and paragraph children
 * from reaching BlockNote's initial document parser.
 */
function sanitizeTableShellBlocks(blocks: PartialBlock[]): PartialBlock[] {
  return blocks.flatMap((block) => {
    const typedBlock = block as PartialBlockWithLegacyTableContent;
    const normalizedChildren = Array.isArray(typedBlock.children)
      ? sanitizeTableShellBlocks(typedBlock.children)
      : typedBlock.children;
    const normalizedContent = INLINE_CONTENT_BLOCK_TYPES.has(
      typedBlock.type ?? '',
    )
      ? sanitizeInlineContent(typedBlock.content)
      : typedBlock.content;
    const tableContent = resolveTableContentFromBlock(typedBlock);
    const normalizedTableContent =
      normalizeRenderableTableContent(tableContent);

    if (typedBlock.type === 'table' && !normalizedTableContent) {
      return [
        {
          ...typedBlock,
          type: 'paragraph',
          content: [],
          children: [],
        } as PartialBlock,
      ];
    }

    if (typedBlock.type === 'table') {
      return [
        {
          ...typedBlock,
          content: normalizedTableContent as PartialBlockContent,
          children: normalizedChildren,
        } as PartialBlock,
      ];
    }

    if (
      (typedBlock.type === 'paragraph' || typedBlock.type === 'heading') &&
      Array.isArray(normalizedChildren) &&
      normalizedChildren.length > 0
    ) {
      return [
        {
          ...typedBlock,
          content: normalizedContent as PartialBlockContent,
          children: [],
        } as PartialBlock,
        ...normalizedChildren,
      ];
    }

    return [
      {
        ...typedBlock,
        content: normalizedContent as PartialBlockContent,
        children:
          typedBlock.type === 'paragraph' || typedBlock.type === 'heading'
            ? []
            : normalizedChildren,
      } as PartialBlock,
    ];
  });
}

/**
 * Prepares editor initialContent before BlockNote creates its document tree.
 * Deferred custom blocks are removed for initial mount and restored later once
 * BlockNote has stable node positions.
 */
export function normalizeInitialEditorContent(
  blocks: PartialBlock[] | undefined,
  editable: boolean,
): PartialBlock[] | undefined {
  if (!blocks) {
    return undefined;
  }

  const withoutDeferredBlocks = filterDeferredCustomBlocks(blocks, editable);

  // Persisted documents can contain invalid table shells from older editor
  // states. Normalize them before BlockNote mounts to avoid position errors.
  const withoutInvalidTableShells = sanitizeTableShellBlocks(
    withoutDeferredBlocks,
  );

  const normalized = editable
    ? withoutInvalidTableShells
    : stripReadOnlyProblemBlocks(withoutInvalidTableShells);

  // BlockNote throws on an empty `initialContent` ("initialContent must be a
  // non-empty array of blocks"), and the throw comes from useCreateBlockNote —
  // the component body — so this component's own error boundary cannot catch
  // it. Callers legitimately arrive empty: a cleared editor seed, or a
  // non-empty document that filterDeferredCustomBlocks reduced to nothing (a
  // phrase of only dropdown/date blocks). `undefined` is BlockNote's own way
  // to say "mount an empty editable document".
  return normalized.length > 0 ? normalized : undefined;
}
